/**
 * SOLID Review
 * S — Strong. One concern: whether the backend is down for long enough to say so — three failed probes in a row, any success clearing
 *     it, a return to the foreground starting clean.
 * O — Good. Another trigger is one more listener.
 * L — Strong. A view model (`IServiceViewModel`).
 * I — Strong. Down or not; the view's one input, the probe.
 * D — Strong. The probe its view hands it, the platform's timers, visibility and page events.
 */
import type { IServiceViewModel } from './IServiceViewModel';
import { ObservableState } from './ObservableState';
import { Reactions } from './Reactions';
import type { IConfigDepViewPlatform } from './platform/IConfigDepViewPlatform';
import type { TClientUseCaseOutput } from '../../application/ports/ClientUseCases.contract';

type IEntityHealthReport = TClientUseCaseOutput<'checkServerHealth'>;

/** The Application's `checkServerHealth`, as the composition root hands it to the banner — a failed call is a failed probe. */
export type TCheckHealth = () => Promise<{ readonly success: true; readonly value: IEntityHealthReport } | { readonly success: false }>;

const POLL_INTERVAL_MS = 5_000;
/**
 * A single failed /health poll is almost always a transient blip (a dropped request, a brief mobile network stall) — not maintenance;
 * showing the banner on the first failure made it flash constantly. Several CONSECUTIVE failures (~15 s of real unreachability) first.
 */
const FAILURE_THRESHOLD = 3;

export interface IStateMaintenance {
  readonly down: boolean;
}

/** What the banner's view hands it: the probe. */
type TMaintenanceInputs = readonly [checkHealth: TCheckHealth];

const UP: IStateMaintenance = { down: false };
const DOWN: Partial<IStateMaintenance> = { down: true };
const BACK_UP: Partial<IStateMaintenance> = { down: false };

/**
 * THE MAINTENANCE BANNER (frontend-framework-oo P5, 2026-10-30, §28.297) — `components/layout/MaintenanceBanner.tsx`'s state and
 * effect. The banner is mounted outside the client core's provider (the 2026-08-17 white screen: the banner that says the backend is
 * unreachable must not itself depend on it), so its probe is a prop; this binds under the view-model provider alone. Coming back from
 * the background the network may take a moment, so the streak is reset and the banner hidden on resume before probing again.
 */
export class MaintenanceViewModel implements IServiceViewModel<IStateMaintenance> {
  private readonly state = new ObservableState<IStateMaintenance>(UP);
  private failures = 0;
  private readonly reactions = new Reactions<TMaintenanceInputs>([{
    deps: (inputs) => inputs,
    run: ([checkHealth]) => {
      const { timers, visibility, pageEvents } = this.platform;
      let cancelled = false;
      const onFailure = (): void => {
        this.failures += 1;
        if (this.failures >= FAILURE_THRESHOLD) this.state.update(DOWN);
      };
      const check = async (): Promise<void> => {
        // The probe never throws: an unreachable server is a REPORT (`reachable: false`), not an exception.
        const answer = await checkHealth();
        /* c8 ignore next -- unmount race during the async probe; not reproducible with act() */
        if (cancelled) return;
        if (answer.success && answer.value.ok) {
          this.failures = 0;
          this.state.update(BACK_UP);
        } else {
          onFailure();
        }
      };
      const onVisible = (): void => {
        if (!visibility.visible()) return;
        this.failures = 0;
        this.state.update(BACK_UP);
        void check();
      };
      void check();
      const id = timers.setInterval(check, POLL_INTERVAL_MS);
      const leave = pageEvents.onDocument('visibilitychange', onVisible);
      return () => {
        cancelled = true;
        timers.clearInterval(id);
        leave();
      };
    },
  }]);

  constructor(private readonly platform: Pick<IConfigDepViewPlatform, 'timers' | 'visibility' | 'pageEvents'>) {}

  getSnapshot(): IStateMaintenance {
    return this.state.get();
  }

  subscribe(listener: () => void): () => void {
    return this.state.subscribe(listener);
  }

  attach(): void {}

  detach(): void {
    this.reactions.stop();
  }

  release(inputs: readonly unknown[]): void {
    this.reactions.stopChanged(inputs as TMaintenanceInputs);
  }

  commit(inputs: readonly unknown[]): void {
    this.reactions.startChanged(inputs as TMaintenanceInputs);
  }
}
